// Port of vanilla buildScriptTxt / buildParamsTxt / buildTimecodesCsv
// (legacy/index.html lines 2838-2920). Pure functions — caller supplies
// all needed context so the helpers can be unit-tested without the store.
//
// The output format matches vanilla EXACTLY (admin-side parsing depends on
// it). In particular:
//   - buildParamsTxt emits `filename` + comma-joined params on a single line
//     (NOT line-per-param), with a trailing blank line between blocks; the
//     final result is .trim()'d.
//   - Vanilla speed is already a multiplier (e.g. 1.00, 1.20) — `(p.speed ||
//     1).toFixed(2)`. The React TtsParamsV2 has no `speed` field today, so
//     callers may pass an extended params object; we read it tolerantly.
//   - Vanilla TTS line includes `Model: …`. v3 appends `Stability Mode: …`
//     (Alt-Takes). v4 (seit 2026-09-29) hat nur Stability + Similarity.
//   - STS uses yes/no (not on/off) and includes `Remove Noise`.
//   - Timecodes start at hour 10, format `10:MM:SS:mmm` (colon-separated ms).
//   - The video-duration prefix row sanitizes the asset title via
//     sanitizeForFilename (vanilla parity).

import type { Block } from '@shared/types';
import { sanitizeForFilename } from './uploadFilenames';

// ─────────────────────────────────────────────────────────────────────────────
// buildScriptTxt
// ─────────────────────────────────────────────────────────────────────────────

export interface ScriptTxtCtx {
  assetType: 'tts' | 'sts' | 'free';
  /** Original asset.script text — used verbatim for STS / free-STS-mode. */
  script: string;
  /** Active blocks for the asset (TTS path). */
  blocks: Block[];
  /** When assetType==='free', the current free-mode for this asset. */
  freeMode?: 'tts' | 'sts';
  /** Set when this is a custom (talent-created) asset; treated as 'free'. */
  isCustom?: boolean;
}

export function buildScriptTxt(ctx: ScriptTxtCtx): string {
  const isStsLike =
    ctx.assetType === 'sts' ||
    ((ctx.assetType === 'free' || ctx.isCustom) && ctx.freeMode === 'sts');
  if (isStsLike) return ctx.script ?? '';
  return ctx.blocks
    .map((b) => b.text?.trim() ?? '')
    .filter(Boolean)
    .join('\n\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// buildParamsTxt
// ─────────────────────────────────────────────────────────────────────────────

/** Tolerant read of params off a Take's paramsSnapshot — vanilla used a
 *  flat `take.params` object that mixed v2/v3/STS keys. */
type RawParams = {
  model?: 'v2' | 'v3' | 'v4' | 'eleven_v3' | 'eleven_v4' | 'eleven_multilingual_v2' | string;
  speed?: number;
  stability?: number;
  similarity?: number;
  style?: number;
  speakerBoost?: boolean;
  removeNoise?: boolean;
  stabilityMode?: string;
  mode?: string;
};

export interface ParamsTxtCtx {
  blocks: Block[];
  voiceName: string;
  /** Per-block filename — caller dispatches TTS vs STS. */
  makeFilename: (blockIdx: number, block: Block) => string;
  /** When true, emit STS-shaped lines for every block (vanilla `'inputBuffer'
   *  in block` sentinel). React Block type is TTS-only, so callers pass this
   *  hint at the asset boundary. Defaults to false (TTS). */
  isSts?: boolean;
}

export function buildParamsTxt(ctx: ParamsTxtCtx): string {
  const lines: string[] = [];

  ctx.blocks.forEach((block, blockIdx) => {
    const take = block.takes[block.favIdx];
    if (!take) return;

    // paramsSnapshot may be TtsParamsV2 | TtsParamsV4 | TtsParamsV3 (React,
    // v3 = Alt-Takes) or a vanilla flat object — all shapes flow through here.
    const snap = (take.paramsSnapshot ?? {}) as RawParams;
    // Pull model from take (React) or params (vanilla); normalize to v2|v3|v4.
    const takeModel = (take as { model?: string }).model;
    const modelRaw = takeModel ?? snap.model ?? 'v2';
    const isV3 =
      modelRaw === 'v3' || modelRaw === 'eleven_v3';
    const isV4 =
      modelRaw === 'v4' || modelRaw === 'eleven_v4';

    lines.push(ctx.makeFilename(blockIdx, block));

    if (ctx.isSts) {
      const parts = [
        `Stability: ${(Number(snap.stability) || 0).toFixed(2)}`,
        `Similarity: ${(Number(snap.similarity) || 0).toFixed(2)}`,
        `Style: ${(Number(snap.style) || 0).toFixed(2)}`,
        `Speaker Boost: ${snap.speakerBoost ? 'yes' : 'no'}`,
        `Remove Noise: ${snap.removeNoise ? 'yes' : 'no'}`,
      ];
      lines.push(parts.join(', '));
    } else if (isV4) {
      lines.push([
        'Model: Eleven v4',
        `Stability: ${(Number(snap.stability) || 0).toFixed(2)}`,
        `Similarity: ${(Number(snap.similarity) || 0).toFixed(2)}`,
      ].join(', '));
    } else {
      const parts = [
        `Model: ${isV3 ? 'Eleven v3' : 'Multilingual v2'}`,
        `Speed: ${(Number(snap.speed) || 1).toFixed(2)}`,
        `Stability: ${(Number(snap.stability) || 0).toFixed(2)}`,
        `Similarity: ${(Number(snap.similarity) || 0).toFixed(2)}`,
        `Style: ${(Number(snap.style) || 0).toFixed(2)}`,
        `Speaker Boost: ${snap.speakerBoost ? 'yes' : 'no'}`,
      ];
      const stabilityMode = snap.stabilityMode ?? snap.mode;
      if (isV3 && stabilityMode) {
        parts.push(`Stability Mode: ${stabilityMode}`);
      }
      lines.push(parts.join(', '));
    }
    lines.push('');
  });

  // Vanilla: `lines.join('\n').trim()` — strips trailing empty line, plus the
  // voice name is NOT emitted (vanilla's buildParamsTxt has no Voice line).
  // Voice metadata is encoded in the filename instead. (Spec sketch was
  // imprecise; vanilla source is the source of truth.)
  void ctx.voiceName;
  return lines.join('\n').trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// buildTimecodesCsv
// ─────────────────────────────────────────────────────────────────────────────

export interface TimecodesCtx {
  assetTitle: string;
  blocks: Block[];
  /** Video duration in seconds — when > 0, prefix a row covering [0, dur]. */
  videoDuration: number;
  /** When true, emit STS-shaped rows: in/out timecode + clip label + take
   *  filename + trim info ("Trim Beginning: 0.4s / Trim Ending: 1.2s").
   *  Required because synthBlocks for STS carry `_stsTakeMeta` with the
   *  source MasterClip's inPoint/outPoint and the full original take
   *  duration. Caller passes `makeStsTakeFilename(meta, block)` so the CSV
   *  references the exact same filename the upload payload uses (block
   *  parameter lets the caller derive voice-suffix for Multi-Voice). */
  isSts?: boolean;
  makeStsTakeFilename?: (
    meta: NonNullable<Block['_stsTakeMeta']>,
    block: Block,
  ) => string;
}

function toTC(sec: number): string {
  const totalMs = Math.round(sec * 1000);
  const m = Math.floor((totalMs / 60000) % 60);
  const s = Math.floor((totalMs / 1000) % 60);
  const ms = totalMs % 1000;
  return `10:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}:${String(ms).padStart(3, '0')}`;
}

/** CSV-escape a value to be wrapped in double quotes: doubles inner quotes. */
function csvEscape(s: string): string {
  return (s ?? '').replace(/"/g, '""');
}

/** Format a fractional-second value as "0.4s" / "0.0s" for the trim-info
 *  CSV column. Always 1 decimal place — matches the resolver/snap-up grid
 *  granularity downstream tools expect. */
function fmtTrimSec(s: number): string {
  const v = Math.max(0, s);
  return `${v.toFixed(1)}s`;
}

/** UTF-8-Byte-Order-Mark. Excel liest eine CSV ohne BOM als Windows-1252 —
 *  Titel und Blocktexte in ar/th/zh/bn/vi wurden zu Zeichensalat (Audit,
 *  Low „Multi-Lingual/Schrift"). */
export const UTF8_BOM = '﻿';

/** Blob für `_TIMECODES.csv`: UTF-8 mit BOM, Trennzeichen bleibt das Komma.
 *
 *  Bewusst NICHT: `sep=,`-Zeile (Excel ignoriert daneben das BOM, die
 *  Umlaute sind wieder kaputt) und kein Wechsel auf `;` (das Format ist ein
 *  Marker-Format mit `10:MM:SS:mmm`-Timecodes; ein Importer im Studio würde
 *  an einem anderen Trennzeichen scheitern). Die Text-Dateien (SKRIPT,
 *  PARAMETERS, ORIGINAL_VIDEO_PATH) bekommen kein BOM: jeder heutige Editor
 *  erkennt UTF-8 ohne, und beim Kopieren ins ElevenLabs-Feld bzw. in die
 *  Pfadzeile würde das unsichtbare Zeichen mitwandern. Details:
 *  decisions.md 2026-09-24. */
export function timecodesCsvBlob(csvText: string): Blob {
  return new Blob([UTF8_BOM + csvText], { type: 'text/csv;charset=utf-8' });
}

export function buildTimecodesCsv(ctx: TimecodesCtx): string | null {
  const hasAny = ctx.blocks.some(
    (b) => b.favIdx >= 0 && !!b.takes[b.favIdx],
  );
  if (!hasAny) return null;

  const lines: string[] = [];

  if (ctx.isSts) {
    // STS shape: per-clip rows with take filename + trim info so the
    // studio editor can reconstruct exactly which slice of which take
    // file was used at each master-timeline position. The same take
    // file may appear multiple times if the user reused it on the
    // master.
    lines.push(
      'in_tc,out_tc,clip_label,take_file,trim_info',
    );
    if (ctx.videoDuration > 0) {
      const sanitized = sanitizeForFilename(ctx.assetTitle || 'Untitled');
      lines.push(
        `${toTC(0)},${toTC(ctx.videoDuration)},"${sanitized}","",""`,
      );
    }
    let clipNum = 0;
    ctx.blocks.forEach((b) => {
      if (b.favIdx < 0 || !b.takes[b.favIdx]) return;
      const meta = b._stsTakeMeta;
      if (!meta) return;
      clipNum += 1;
      const inP = meta.inPoint ?? 0;
      const outP = meta.outPoint ?? meta.fullDuration ?? 0;
      const clipDur = Math.max(0, outP - inP);
      const start = b.offset || 0;
      const end = start + clipDur;
      const takeFile = ctx.makeStsTakeFilename
        ? ctx.makeStsTakeFilename(meta, b)
        : `Take${meta.session}-${meta.takeInSession}.mp3`;
      const trimEnd = Math.max(0, (meta.fullDuration ?? outP) - outP);
      const trimInfo =
        `Trim Beginning: ${fmtTrimSec(inP)} / Trim Ending: ${fmtTrimSec(trimEnd)}`;
      lines.push(
        `${toTC(start)},${toTC(end)},"Clip ${clipNum}","${csvEscape(takeFile)}","${csvEscape(trimInfo)}"`,
      );
    });
    return lines.length > 1 ? lines.join('\n') + '\n' : null;
  }

  // TTS shape: in/out + BLOCK_N + block text.
  if (ctx.videoDuration > 0) {
    const sanitized = sanitizeForFilename(ctx.assetTitle || 'Untitled');
    // 4th column kept empty on the asset/duration row for shape consistency.
    lines.push(`${toTC(0)},${toTC(ctx.videoDuration)},"${sanitized}",""`);
  }

  ctx.blocks.forEach((b, i) => {
    if (b.favIdx < 0 || !b.takes[b.favIdx]) return;
    const fav = b.takes[b.favIdx];
    const dur = fav.duration || 0;
    const start = b.offset || 0;
    const end = start + dur;
    lines.push(
      `${toTC(start)},${toTC(end)},"BLOCK_${i + 1}","${csvEscape(b.text ?? '')}"`,
    );
  });

  return lines.length > 0 ? lines.join('\n') + '\n' : null;
}

/** `_ORIGINAL_VIDEO_PATH.txt` — hält bei einem vom Admin vorgegebenen
 *  Cloud-Video fest, welches Original verlinkt war. `videoPath` liegt
 *  URL-kodiert und relativ zu `Kunden/` in der Config; die Datei trägt ihn
 *  lesbar ab `Kunden/`. Ein nicht dekodierbares Segment bleibt, wie es ist. */
export function buildOriginalVideoPathTxt(videoPath: string): string {
  const decoded = videoPath.split('/').filter(Boolean).map((seg) => {
    try { return decodeURIComponent(seg); } catch { return seg; }
  }).join('/');
  return `Kunden/${decoded}\n`;
}

